import { Breadcrumb, useComputed, WorkflowLayout, WorkflowLayoutStep } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useNavigate } from "react-router-dom";
import { useRouteParams } from "src/hooks/useRouteParams";
import { guidedWorkflows } from "src/routes/projects/change-events/guided/workflows/registry";
import { createHierarchyBreadcrumbs } from "src/routes/projects/ProjectHeader";
import { ChangeEventParams } from "src/routes/routesDef";
import { createChangeEventsUrl } from "src/RouteUrls";
import { fail, queryResult } from "src/utils";
import {
  GuidedChangeEventPage_ChangeEventFragment,
  useGuidedChangeEventPage_ChangeEventQuery,
} from "./GuidedChangeEventPage.generated";

export function GuidedChangeEventPage() {
  const { changeEventId } = useRouteParams<ChangeEventParams>();
  const query = useGuidedChangeEventPage_ChangeEventQuery({ variables: { changeEventId }, fetchPolicy: "cache-first" });
  return queryResult(query, ({ changeEvent }) => <GuidedChangeEventPageView changeEvent={changeEvent} />);
}

type GuidedChangeEventPageViewProps = { changeEvent: GuidedChangeEventPage_ChangeEventFragment };

function GuidedChangeEventPageView(props: GuidedChangeEventPageViewProps) {
  const { changeEvent } = props;
  const navigate = useNavigate();
  const project = changeEvent.projectStage.project;
  const workflow = guidedWorkflows[changeEvent.workflow?.code ?? fail("This is not a guided change event")];
  const formConfig = workflow?.formConfig ?? fail("Coming soon.");

  const breadcrumbs: Breadcrumb[] = createHierarchyBreadcrumbs(project, createChangeEventsUrl).map((crumb) => ({
    label: crumb.label,
    href: crumb.href,
  }));

  const form = useFormState({ config: formConfig });
  const actions = workflow.useActions({ form, projectId: project.id });

  // Every step comes from the workflow, and each one is gated behind the previous step's completion.
  const steps = useComputed<WorkflowLayoutStep[]>(() => {
    const validities = workflow.steps.map((s) => s.isComplete(form));
    return workflow.steps.map((s, i): WorkflowLayoutStep => {
      const explicitlyDisabled = s.disabled?.(form) ?? false;
      // The first step is always reachable; the rest wait on the step before them.
      const gatedByPrevious = i > 0 && !validities[i - 1];
      return {
        label: s.label,
        isValid: validities[i],
        disabled: explicitlyDisabled || gatedByPrevious,
        content: s.render(form),
        onContinue: () => actions.onContinue?.[s.value]?.(),
      };
    });
  }, [form, workflow]);

  return (
    <WorkflowLayout
      title="Create Change Event"
      documentTitleSuffix={project.name}
      breadcrumbs={{ breadcrumbs }}
      steps={steps}
      onCancel={() => navigate(createChangeEventsUrl(project.id))}
      onSaveAndExit={actions.onSaveAndExit}
      completeLabel="Create"
      onComplete={() => actions.onSubmit()}
      isDirty={() => form.dirty}
    />
  );
}
